<template>
   <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <Notification :notify="notify" />
      <UploadingStatus ref="uploadingStatus" />
      <ConfirmationDialog ref="confirm" />
      <v-toolbar dark color="primary" dense>
        <v-toolbar-title class="text-body-1">
             Update Publication Data
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <v-divider></v-divider>
        <h2 class="my-2">Select Columns to Update</h2>
        <div class="row">
        <div
          class="col checkbox"
          v-for="(checked,key) in exportColumns"
          :key="key"
        >
          <v-checkbox
            :id="'checkbox-' + key"
            v-model="checkboxes[key]"
            :label="exportColumns[key]"
            :disabled="key==='ISBN'"
            class="checkbox"
          />
        </div>
      </div>
        <h2 class="my-2">Download Selected Columns</h2>
        <div class="row">
          <div class="col d-flex align-center">
                <v-btn color="primary" @click="onDownloadPublications()" :disabled="Object.keys(checkboxes).some(key => checkboxes[key]) === false">
                Download CSV
                </v-btn>
            </div>
        </div>
        <h2 class="my-2">Upload CSV For Selected Columns</h2>
        <div class="row">
          <div class="col">
                <v-file-input
                    ref="dataUpload"
                    id="dataUpload"
                    accept=".csv"
                    placeholder="Upload a csv file"
                    prepend-icon="mdi-file-excel"
                    label="Data Upload"
                    @change="onDataUpload"
                    ></v-file-input>
            </div>
        </div>
      </v-card-text>
      <v-card-actions class="pt-3">
        <v-spacer></v-spacer>
        <v-btn @click.native="cancel" v-if="!options.noconfirm"> Close </v-btn>
      </v-card-actions>
      <v-overlay :value="overlay">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>
<script>
import * as api from "@/api";
import csv from 'csvtojson';
import { Parser } from "json2csv";
import fileUploadService from "../api/fileUploadService";
import Notification from "@/components/Notification";
import UploadingStatus from "@/components/UploadingStatus";
import { computed } from "vue";
import ConfirmationDialog from "@/components/ConfirmationDialog";

export default {
  name: 'UploadingDialog',
  components: {
    Notification,
    UploadingStatus,
    ConfirmationDialog,
  },
  computed: {},
  created() {
    api.getAll("publications/allowed-languages").then((response) => {
      this.allowedLanguages = response.data;
    }).catch((error) => {
      console.error("Error loading allowed languages:", error);
    });
    this.getSubjects();
  },
  methods: {
    open() {
      this.dialog = true;
    },
    cancel() {
      this.dialog = false;
      this.checkboxes = {
        ISBN: true,
      };
      this.notify = {
        type: 0,
        message: "",
        errors: [],
      };
    },
    onDownloadPublications() {
      if (Object.keys(this.checkboxes).length <= 0) {
        this.notify.type = 4;
        this.notify.errors.push("Please select Data column to download.");
        return;
      }
      api.getAll("publications").then((response) => {
        this.exportData = response.data;
        this.downloadPublicationMetadataAsCSV(this.exportData);
      });
    },
    downloadPublicationMetadataAsCSV(json) {
      const parser = new Parser();
      const csv = parser.parse(
        json.map((pub) => this.mapPublicationMetadataToCSVRecord(pub))
      );

      var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      var link = document.createElement("a");
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "publications-metadata.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    mapPublicationMetadataToCSVRecord(pub) {
      const defaultColumns = [
        "Title",
        "ISBN",
        "PublisherName"
    ];
      const columnConfig = computed(() =>
        Object.fromEntries(
          Object.entries(this.checkboxes).filter(([_, checked]) => checked)
        )
      );
      const selectedColumns = Object.keys(columnConfig.value);
      return Object.fromEntries(
        Array.from(new Set([...defaultColumns, ...selectedColumns]))
          .filter(key => key in pub)
          .flatMap(key => {
            if (key === "allowedLanguages") {
              return [
                // ["Translatable", pub.isTranslatable],
                ["Allowed Languages", this.getLanguageName(pub.allowedLanguages)],
              ];
            }
            if (key === "subject1" || key === "subject2") {
              return [[this.exportColumns[key], pub[key] ? pub[key].SubjectName : null]];
            }

            return [[this.exportColumns[key], pub[key]]];
          })
      );
    },
    async onDataUpload(file) {
      if (this._uploading) return;
      this._uploading = true;

      this.overlay = true;
      const columnConfig = computed(() =>
        Object.fromEntries(
          Object.entries(this.checkboxes).filter(([_, checked]) => checked)
        )
      );
      if (!file) {
        this.overlay = false;
        this._uploading = false;
        return;
      }
      if (Object.keys(columnConfig.value).length <= 1) {
        this.notify.type = 4;
        this.notify.errors.push("Please select Data column to upload.");
        this.overlay = false;
        this._uploading = false;
        if (input) input.value = null;
        this.$refs.dataUpload.reset();
        this._uploading = false;
        return;
      }

      const confirmed = await this.$refs.confirm.open(
        "Confirm",
        "Are you sure you want to upload this file?"
      );

      if (!confirmed) {
        this.overlay = false;
        const input = this.$refs.dataUpload.$el.querySelector('input');
        if (input) input.value = null;
        this.$refs.dataUpload.reset();
        this._uploading = false;
        return;
      }

      try {
        const content = await fileUploadService.uploadFileReader(file);
        const uploadContent = await this.CsvToJson(content);
        const payloadKeys = [];
        for (const key in columnConfig.value) {
          if (key !== "allowedLanguages") {
            if (uploadContent[0].hasOwnProperty(this.exportColumns[key]) === false) {
              this.notify.type = 4;
              this.notify.errors.push(`${this.exportColumns[key]} column is required for upload.`);
              this.overlay = false;
              this._uploading = false;
              if (input) input.value = null;
              this.$refs.dataUpload.reset();
              return;
            } else {
              payloadKeys.push(key);
            }
          }
        }

        if (columnConfig.value.allowedLanguages) {
          //  if (uploadContent[0].hasOwnProperty('Translatable') === false) {
          //   this.notify.type = 4;
          //   this.notify.errors.push("Translatable column is required for upload.");
          //   this.overlay = false;
          //   this._uploading = false;
          //   if (input) input.value = null;
          //   this.$refs.dataUpload.reset();
          //   return;
          // }
          if (uploadContent[0].hasOwnProperty('Allowed Languages') === false) {
            this.notify.type = 4;
            this.notify.errors.push("Allowed Languages column is required for upload.");
            this.overlay = false;
            this._uploading = false;
            if (input) input.value = null;
            this.$refs.dataUpload.reset();
            return;
          }
          // payloadKeys.push("isTranslatable");
          payloadKeys.push("allowedLanguages");
        }

        var validations = [];
        const payloadContent = (uploadContent || [])
          .filter(e => {
            let isValid = true;
            let validTranslatableFlag = false;

            for (const key of payloadKeys) {
              if (key === "ISBN" && !this.isColumnNumber(e['ISBN'])) {
                validations.push({
                  Title: e['Title'],
                  ISBN: e['ISBN'],
                  PublisherName: e['Publisher Name'],
                  Message: 'Invalid ISBN',
                  isSuccess: false
                });
                isValid = false;
                break;
              }

              // if (key === "listprice" && this.isColumnNotEmpty(e[this.exportColumns['listprice']])) {
              //   if (!this.isColumnNumber(e[this.exportColumns['listprice']])) {
              //     validations.push({
              //       Title: e['Title'],
              //       ISBN: e['ISBN'],
              //       PublisherName: e['Publisher Name'],
              //       Message: 'Invalid List Price',
              //       isSuccess: false
              //     });
              //     isValid = false;
              //     break;
              //   }
              // }
              // if (key === "discount" && this.isColumnNotEmpty(e[this.exportColumns['discount']])) {
              //   if (!this.isColumnNumber(e[this.exportColumns['discount']])) {
              //     validations.push({
              //       Title: e['Title'],
              //       ISBN: e['ISBN'],
              //       PublisherName: e['Publisher Name'],
              //       Message: 'Invalid Discount',
              //     isSuccess: false
              //   });
              //   isValid = false;
              //   break;
              //   }
              // }
              if (key === "PublishedDate" && !this.isValidYear(e['Published Date'])) {
                validations.push({
                  Title: e['Title'],
                  ISBN: e['ISBN'],
                  PublisherName: e['Publisher Name'],
                  Message: 'Invalid Published Date',
                  isSuccess: false
                });
                isValid = false;
                break;
              }
              if (key === "allowedLanguages") {
                validTranslatableFlag = this.isValidTranslatable(
                  // e['Translatable'].toLowerCase() === 'true' ? true : e['Translatable'].toLowerCase() === 'false' ? false : e['Translatable'],
                  e['Allowed Languages'],
                  validations,
                  e
                );
                if (!validTranslatableFlag) {
                  isValid = false;
                  break;
                }
              }
              if (key === "subject1" && this.isColumnNotEmpty(e[this.exportColumns['subject1']])) {
                if (!this.isValidSubject(e[this.exportColumns['subject1']])) {
                  validations.push({
                    Title: e['Title'],
                    ISBN: e['ISBN'],
                    PublisherName: e['Publisher Name'],
                    Message: 'Invalid Subject 1',
                    isSuccess: false
                  });
                  isValid = false;
                  break;
                }
              }
              if (key === "subject2" && this.isColumnNotEmpty(e[this.exportColumns['subject2']])) {
                if (!this.isValidSubject(e[this.exportColumns['subject2']])) {
                  validations.push({
                    Title: e['Title'],
                    ISBN: e['ISBN'],
                    PublisherName: e['Publisher Name'],
                    Message: 'Invalid Subject 2',
                    isSuccess: false
                  });
                  isValid = false;
                  break;
                }
              }
            }
            return isValid; 
          })
          .map(e => {
            const record = {};
            for (const key of payloadKeys) {
              if (key === "allowedLanguages") {
                record["isTranslatable"] = e.isTranslatable;
                record[key] = e.allowedLanguages;
                continue;
              }
              if (key === "subject1" || key === "subject2") {
                const subject = this.subjects.find(s => s.SubjectName.toLowerCase() === e[this.exportColumns[key]].toLowerCase());
                record[key + "Id"] = subject ? subject.id : null;
                continue;
              }

              record[key] = e[this.exportColumns[key]];
            }
            return record;
          });

        this.loading = true;
        if (payloadContent.length > 0) {
          const response = await api.update("publications/update-bulk", { payloadContent });

          if (response.status === 200 || response.status === 201) {
            this.$refs.uploadingStatus.open([...response.data, ...validations]);
          }
        } else {
          this.$refs.uploadingStatus.open(validations);
        }
      } catch (error) {
        this.notify.type = 4;
        this.notify.errors.push("Failed to upload data. Please try again.");
        console.error("Upload Error:", error);
      } finally {
        this.loading = false;
        this.overlay = false;
        const input = this.$refs.dataUpload.$el.querySelector('input');
        if (input) input.value = null;
        this.$refs.dataUpload.reset();
        this._uploading = false;
      }
    },
    checkValidLanguage(lang) {
      const invalidLanguages = [];
      const languages = lang.split(',').map(l => l.trim().toLowerCase());

      languages.forEach(language => {
        if (language.toLowerCase() == "none") invalidLanguages.push("none");
        if (language.toLowerCase() == "all bt") return;
        const isValid = this.allowedLanguages.some(
          allowedLang => allowedLang.name.toLowerCase() === language
        );
        if (!isValid) {
          invalidLanguages.push(language);
        }
      });
      return invalidLanguages;
    },
    async CsvToJson(content) {
      return new Promise((resolve,reject)=> {
        csv({
            noheader:false,
            output: "json",
            quote: '"',
            flatKeys: false,
            downstreamFormat: true
        }).fromString(content)
        .then((csvRow)=>{ 
            resolve(csvRow);
        })
      });    
    },
    getLanguageCode(languageName) {
      const languages = languageName.split(',').map(l => l.trim());
      const languageCodes = languages.map(language => {
        if (language.toLowerCase() == "none") return "none";
        if (language.toLowerCase() == "all bt") return "all bt";
        const langObj = this.allowedLanguages.find(
          allowedLang => allowedLang.name.toLowerCase() === language.toLowerCase()
        );
        return langObj ? langObj.code : null;
      }).filter(code => code !== null);
      return languageCodes.join(', ');
    },
    getLanguageName(languageCode) {
      const codes = languageCode.split(',').map(c => c.trim());
      const languageNames = codes.map(code => {
        if (code.toLowerCase() == "none") return "None";
        if (code.toLowerCase() == "all bt") return "All BT";
        const langObj = this.allowedLanguages.find(
          allowedLang => allowedLang.code.toLowerCase() === code.toLowerCase()
        );
        return langObj ? langObj.name : null;
      }).filter(name => name !== null);
      return languageNames.join(', ');
    },
    isValidYear(value) {
      const yearStr = String(value).trim();
      if (!/^\d{4}$/.test(yearStr)) return false;

      const year = Number(yearStr);
      return year <= new Date().getFullYear();
    },
    isColumnNotEmpty(value) {
      if (!value || value.trim() === "") {
        return false;
      }
      return true;
    },
    isColumnNumber(value) {
      if (!value || value.trim() === "") {
        return false;
      }
      if (isNaN(Number(value))) {
        return false;
      }
      return true;
    },
    isValidTranslatable(allowedLanguages, languageValidation, item) {
      // if (typeof isTranslatable !== 'boolean') {
      //   languageValidation.push({Title: item.Title, ISBN: item.ISBN, PublisherName: item.PublisherName, Message: `Invalid translatable value`, isSuccess: false  });
      //   return false; 
      // }

      // if (isTranslatable){
        if (allowedLanguages.trim() === "") {
          languageValidation.push({Title: item.Title, ISBN: item.ISBN, PublisherName: item.PublisherName, Message: `Languages Cannot be Empty with translatable true`, isSuccess: false  });
          return false;
        }
        const invalidLangs = this.checkValidLanguage(allowedLanguages);

        if (invalidLangs.includes("none")) {
          item.isTranslatable = false;
          item.allowedLanguages = this.getLanguageCode("None");
          return true;
        }

        if(allowedLanguages.split(",").map(lang => lang.trim().toLocaleLowerCase()).includes("all bt") && allowedLanguages.split(",").length > 1) {
          languageValidation.push({Title: item.Title, ISBN: item.ISBN, PublisherName: item.PublisherName, Message: `Cannot have 'All BT' with other languages when translatable is true`, isSuccess: false  });
          return false;
        }

        if (invalidLangs.length > 0) {
          languageValidation.push({Title: item.Title, ISBN: item.ISBN, PublisherName: item.PublisherName, Message: `Invalid Languages: ${invalidLangs.join(', ')}`, isSuccess: false  });
          return false;
        } else {
          item.isTranslatable = true;
          item.allowedLanguages = this.getLanguageCode(allowedLanguages);
          return true;
        }
      // } else {
      //   item.allowedLanguages = this.getLanguageCode("None");
      //   return true;
      // }
    },
    isValidSubject(subject) {
      if (!subject || subject.trim() === "") {
        return true; 
      }
      return this.subjects.map(s => s.SubjectName.toLowerCase()).includes(subject.toLowerCase());
    },
    getSubjects() {
      this.subjects = [];
      new Promise((resolve, reject) => {
        api
          .getAll("subject")
          .then((response) => {
            this.subjects = response.data;
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
  props: {
  },
  computed: {
  },
  watch: {
    notify() {
    },
  },
  data() {
    return {
      list: [],
      overlay: false,
      downloadType: "",
      checkboxes: {
        ISBN: true,
      },
      exportData: [],
      exportColumns: {
        Title: "Title",
        ISBN: "ISBN",
        // AuthorName: "Author Name",
        PublisherName: "Publisher Name",
        PublishedDate: "Published Date",
        CityOfPublication: "City Of Publication",
        CountryOfPublication: "Country Of Publication",
        keywords: "Keywords",
        Summary: "Summary",
        // listprice: "List Price",
        // discount: "Discount",
        countries_allowed: "Countries Allowed",
        // bisac: "Bisac",
        // lcc: "Lcc",
        // dewey: "Dewey",
        subject1: "Subject 1",
        subject2: "Subject 2",
        language: "Language",
        allowedLanguages: "Allowed Languages",
        // isTranslatable: "Translatable",
        "contributorRole1": "Contributor Role 1",
        "contributor1": "Contributor 1",
        "contributorRole2": "Contributor Role 2",
        "contributor2": "Contributor 2",
        "contributorRole3": "Contributor Role 3",
        "contributor3": "Contributor 3",
        // "contributorRole4": "Contributor Role 4",
        // "contributor4": "Contributor 4",
        // "contributorRole5": "Contributor Role 5",
        // "contributor5": "Contributor 5",
        // "contributorRole6": "Contributor Role 6",
        // "contributor6": "Contributor 6",
        // "contributorRole7": "Contributor Role 7",
        // "contributor7": "Contributor 7",
        // "contributorRole8": "Contributor Role 8",
        // "contributor8": "Contributor 8",
        // "contributorRole9": "Contributor Role 9",
        // "contributor9": "Contributor 9",
        // "contributorRole10": "Contributor Role 10",
        // "contributor10": "Contributor 10",
      },
      dialog: false,
      allowedLanguages: [],
      notify: {
        type: 0,
        message: "",
        errors: [],
      },
      options: {
        color: "primary",
        width: 1200,
        zIndex: 200,
        noconfirm: false,
      },
    };
  },
};
</script>

<style scoped>
  .checkbox {
    min-width: 200px;
    max-width: 200px;
  }
</style>


// WEBPACK FOOTER //
// src/components/UploadingDialog.vue